import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type { Run, DateRange } from '../types';

interface DateFilterContextValue {
  dateRange: DateRange;
  dateFrom: string;
  dateTo: string;
  setDateRange: (range: DateRange) => void;
  setDateFrom: (d: string) => void;
  setDateTo: (d: string) => void;
  filterRuns: (runs: Run[]) => Run[];
  label: string;
  /** YYYY-MM of the latest loaded run (the month "This month" refers to). */
  monthPrefix: string;
}

const DateFilterContext = createContext<DateFilterContextValue>({
  dateRange: 'month',
  dateFrom: '',
  dateTo: '',
  setDateRange: () => undefined,
  setDateFrom: () => undefined,
  setDateTo: () => undefined,
  filterRuns: r => r,
  label: 'This month',
  monthPrefix: new Date().toISOString().slice(0, 7),
});

function getMonthPrefix(runs: Run[]): string {
  if (runs.length === 0) return new Date().toISOString().slice(0, 7);
  return runs.reduce((max, r) => (r.date > max ? r.date : max), runs[0].date).slice(0, 7);
}

function toDateStr(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function DateFilterProvider({
  children,
  allRuns,
}: {
  children: ReactNode;
  allRuns: Run[];
}) {
  const [dateRange, setDateRangeState] = useState<DateRange>('month');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const setDateRange = useCallback((range: DateRange) => {
    setDateRangeState(range);
  }, []);

  const filterRuns = useCallback(
    (runs: Run[]): Run[] => {
      if (dateRange === '') return runs;

      if (dateRange === 'month') {
        const prefix = getMonthPrefix(allRuns);
        return runs.filter(r => r.date.startsWith(prefix));
      }

      const now = new Date();
      if (dateRange === '7') {
        const cutoff = toDateStr(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000));
        return runs.filter(r => r.date >= cutoff);
      }

      if (dateRange === '30') {
        const cutoff = toDateStr(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
        return runs.filter(r => r.date >= cutoff);
      }

      if (dateRange === 'custom') {
        return runs.filter(r => {
          if (dateFrom && r.date < dateFrom) return false;
          if (dateTo && r.date > dateTo) return false;
          return true;
        });
      }

      return runs;
    },
    [dateRange, dateFrom, dateTo, allRuns],
  );

  const monthPrefix = useMemo(() => getMonthPrefix(allRuns), [allRuns]);

  const label = useMemo(() => {
    if (dateRange === 'month') {
      const prefix = monthPrefix;
      const [year, month] = prefix.split('-');
      const d = new Date(parseInt(year), parseInt(month) - 1, 1);
      return d.toLocaleString('default', { month: 'long', year: 'numeric' });
    }
    if (dateRange === '7') return 'Last 7d';
    if (dateRange === '30') return 'Last 30d';
    if (dateRange === 'custom') return dateFrom && dateTo ? `${dateFrom} – ${dateTo}` : 'Custom';
    return 'All time';
  }, [dateRange, dateFrom, dateTo, monthPrefix]);

  return (
    <DateFilterContext.Provider
      value={{ dateRange, dateFrom, dateTo, setDateRange, setDateFrom, setDateTo, filterRuns, label, monthPrefix }}
    >
      {children}
    </DateFilterContext.Provider>
  );
}

export function useDateFilter(): DateFilterContextValue {
  return useContext(DateFilterContext);
}
